import {
  Deal,
  OrderValues,
  RemoteConfig,
  VariableValue,
} from '@ca/common/types'
import { Discountable, Discounts } from '../discounts/Discounts'
import {
  BundleSummaryItem,
  getOrderItemSummary,
  OrderSummaryItem,
  SummableItem,
} from './getOrderItemSummary'
import { OrderState } from '@order/hooks/state/orderState'
import { groupBy } from 'lodash'
import {
  BundleType,
  OrderFrame,
  SelectedPackage,
  StepMode,
  StepsState,
} from '@order/orderTypes'

export const getItemSummary = <T extends SummableItem>({
  item,
  config,
  getItemValues,
}: {
  item: T
  config: RemoteConfig
  getItemValues: GetItemValues
}) => {
  const values = getItemValues({
    bundleType: item.bundleType,
    state: item.steps,
    frame: item?.frame,
  })
  const elements = getOrderItemSummary(item, config, values)
  const price = Discounts.sumItemsWithPrices(elements.values())

  return {
    price,
    id: item.id,
    elements,
    item,
    values,
  }
}

export type ItemSummary<T extends SummableItem> = ReturnType<
  typeof getItemSummary<T>
>

export type ItemTranslationExtra = {
  path?: string | null
  type: VariableValue
}

export type OrderElementExtra = {
  translation?: ItemTranslationExtra
  [key: string]: any
}

export type OrderElement = {
  variable: string // frame, index, coating
  value: string | number // frameId, o150, HC,
  extra?: OrderElementExtra
  summary: boolean
  price: Discountable
}

export type ItemValuesParams = {
  frame: OrderFrame | undefined
  bundleType?: BundleType
  state?: StepsState
}

export type GetItemValues = (p: ItemValuesParams) => OrderValues

export type OrderParams = {
  order: OrderState
  deals: Record<BundleType, Deal | undefined>
  packageConfigs: Partial<Record<BundleType, SelectedPackage>>
  config: RemoteConfig
  getItemValues: GetItemValues
  steps: Record<string, Partial<Record<StepMode, StepsState>>>
}

export type OrderPriceSummary = {
  price: Discountable
  items: ItemSummary<OrderSummaryItem | BundleSummaryItem>[]
}

// This gets non discounted price summary of the order
export const getOrderPriceSummary = ({
  order,
  deals,
  packageConfigs,
  config,
  getItemValues,
  steps,
}: OrderParams): OrderPriceSummary => {
  const bundleKeys = Object.keys(order.bundles) as BundleType[]
  const bundles: Partial<
    Record<BundleType, ReturnType<typeof getItemSummary>>
  > = {}
  bundleKeys.forEach((key) => {
    const item = order.bundles[key]
    if (!item) {
      return
    }
    const packageId = packageConfigs[key]?.id

    const orderItems = order.items.filter((i) => i.bundleType === key)
    const frame = orderItems[0]?.frame

    bundles[key] = getItemSummary({
      item: {
        id: key,
        summary: 'bundle',
        deal: deals[key],
        frame,
        bundleType: key,
        // packageVariantId: packageConfigs[key]?.variantId,
        packageCfg: packageId ? config.packages[packageId] : undefined,
        ...item,
      } satisfies BundleSummaryItem,
      config,
      getItemValues,
    })
  })

  const resolveSteps = (id: string) => {
    if (!steps[id]) {
      return []
    }
    const all = steps[id]
    const values = Object.values(all)
    return values.flat()
  }

  const items = order.items.map((item) => {
    return getItemSummary({
      item: {
        ...item,
        deal: deals[item.bundleType],
        summary: 'frame',
        steps: resolveSteps(item.id),
      } satisfies OrderSummaryItem,
      config,
      getItemValues,
    })
  })
  const grouped = groupBy(items, 'item.bundleType')
  // Keep the order
  const groups = ['base', 'extra'] as const

  const allItems = groups
    .flatMap((g) => {
      const items = grouped[g]
      if (!items) {
        return []
      }
      return [bundles[g], ...items]
    })
    .filter((i) => !!i)

  const total = Discounts.sumItemsWithPrices(allItems)

  return {
    price: total,
    items: allItems,
  }
}
